import {
  type AgentEvent,
  AgentEventsEnum,
  type ConnectionQuality,
  LiveAvatarSession,
  type SessionDisconnectReason,
  SessionEvent,
  SessionState,
} from '@heygen/liveavatar-web-sdk';
import { useCallback, useEffect, useRef } from 'react';
import logger from '../../../utils/logger';
import { useStreamingAvatarContext } from '../context/StreamingAvatarContext';
import { useVoiceChat } from './useVoiceChat';

export const useStreamingAvatarSession = () => {
  const {
    avatarRef,
    basePath,
    sessionState,
    setSessionState,
    stream,
    setStream,
    setIsListening,
    setIsUserTalking,
    setIsAvatarTalking,
    setConnectionQuality,
    sessionCloseReason,
    setSessionCloseReason,
    handleUserTalkingMessage,
    handleStreamingTalkingMessage,

    clearMessages,
    setWsUrl,
  } = useStreamingAvatarContext();
  const { stopVoiceChat } = useVoiceChat();

  // Ref to hold the latest stopWithReason function to avoid circular dependency
  const stopWithReasonRef = useRef<
    ((reason: 'user' | 'idle_timeout' | 'credit_limit' | 'unexpected') => Promise<void>) | null
  >(null);

  // Initialize avatar with token
  const init = useCallback(
    (token: string) => {
      // Create new session instance with token
      // Config is optional, passing empty object or specific defaults if needed
      avatarRef.current = new LiveAvatarSession(token, {
        apiUrl: basePath, // Use basePath if provided (though new SDK usually handles this internally)
      });

      return avatarRef.current;
    },
    [basePath, avatarRef],
  );

  // Handle stream ready event - New SDK flow
  // We don't get the MediaStream directly, but signal readiness
  // The component should listen to this or rely on session state
  const handleStreamReady = useCallback(() => {
    // We don't have the stream object here in the new SDK
    // The Video attachment is handled via avatar.attach(element)
    // We set state to CONNECTED to trigger UI updates
    logger.info('HeyGenAvatar', 'Stream ready signal received');
    setSessionState(SessionState.CONNECTED);
  }, [setSessionState]);

  // Handle unexpected disconnection (idle timeout or network issue)
  const handleUnexpectedDisconnect = useCallback((reason: SessionDisconnectReason) => {
    logger.info('HeyGenAvatar', 'Unexpected disconnect', { reason });
    // Map SDK reason to app reason
    const appReason: 'idle_timeout' | 'unexpected' = 'unexpected';

    // Assuming IDLE_TIMEOUT might be a reason (checking types would confirm, but "unexpected" is safe default)
    // New SDK might have specific enum for idle

    if (stopWithReasonRef.current) {
      stopWithReasonRef.current(appReason);
    }
  }, []);

  // Stop avatar session with a specific reason
  const stopWithReason = useCallback(
    async (reason: 'user' | 'idle_timeout' | 'credit_limit' | 'unexpected') => {
      // Remove specific listeners if needed, but instance will be discarded
      if (avatarRef.current) {
        // Off methods might be different, but typically we just stop
        // New SDK extends TypedEmitter, so .off works if needed
      }

      clearMessages();
      stopVoiceChat();
      setIsListening(false);
      setIsUserTalking(false);
      setIsAvatarTalking(false);
      setStream(null);
      setSessionCloseReason(reason);

      try {
        await avatarRef.current?.stop();
      } catch (_e) {
        // Ignore stop errors
      }

      setSessionState(SessionState.INACTIVE);
    },
    [
      setSessionState,
      setStream,
      avatarRef,
      setIsListening,
      stopVoiceChat,
      clearMessages,
      setIsUserTalking,
      setIsAvatarTalking,
      setSessionCloseReason,
    ],
  );

  // Update the ref whenever stopWithReason changes
  useEffect(() => {
    stopWithReasonRef.current = stopWithReason;
  }, [stopWithReason]);

  // Stop avatar session — defaults to 'user' reason unless overridden (e.g. 'credit_limit')
  const stop = useCallback(
    async (reason?: 'user' | 'idle_timeout' | 'credit_limit' | 'unexpected') => {
      await stopWithReason(reason ?? 'user');
    },
    [stopWithReason],
  );

  // Start avatar session
  const start = useCallback(
    // async (token?: string) => {
    async (token?: string, _options: { max_session_duration?: number } = {}) => {
      if (sessionState !== SessionState.INACTIVE) {
        throw new Error('There is already an active session');
      }

      if (!avatarRef.current) {
        if (!token) {
          throw new Error('Token is required');
        }
        init(token);
      }

      if (!avatarRef.current) {
        throw new Error('Avatar is not initialized');
      }

      setSessionState(SessionState.CONNECTING);

      // Register event listeners
      const avatar = avatarRef.current;

      avatar.on(SessionEvent.SESSION_STREAM_READY, handleStreamReady);
      avatar.on(SessionEvent.SESSION_DISCONNECTED, handleUnexpectedDisconnect);
      avatar.on(SessionEvent.SESSION_CONNECTION_QUALITY_CHANGED, (quality: ConnectionQuality) =>
        setConnectionQuality(quality),
      );

      // Agent Events
      avatar.on(AgentEventsEnum.USER_SPEAK_STARTED, () => setIsUserTalking(true));
      avatar.on(AgentEventsEnum.USER_SPEAK_ENDED, () => setIsUserTalking(false));
      avatar.on(AgentEventsEnum.AVATAR_SPEAK_STARTED, () => setIsAvatarTalking(true));
      avatar.on(AgentEventsEnum.AVATAR_SPEAK_ENDED, () => setIsAvatarTalking(false));

      avatar.on(AgentEventsEnum.USER_TRANSCRIPTION, (event: AgentEvent) => {
        handleUserTalkingMessage((event as AgentEvent & { text: string }).text);
      });

      avatar.on(AgentEventsEnum.AVATAR_TRANSCRIPTION, (event: AgentEvent) => {
        handleStreamingTalkingMessage((event as AgentEvent & { text: string }).text);
      });

      // Start the session
      const sessionData = await avatar.start();

      // Capture ws_url for Custom Mode events if available
      const sessionResponse = sessionData as unknown as {
        ws_url?: string;
        data?: { ws_url?: string };
      };
      if (sessionResponse?.ws_url) {
        setWsUrl(sessionResponse.ws_url);
      } else if (sessionResponse?.data?.ws_url) {
        setWsUrl(sessionResponse.data.ws_url);
      }

      return avatar;
    },
    [
      init,
      handleStreamReady,
      handleUnexpectedDisconnect,
      setSessionState,
      avatarRef,
      sessionState,
      setConnectionQuality,
      setIsUserTalking,
      handleUserTalkingMessage,
      handleStreamingTalkingMessage,
      setIsAvatarTalking,
      setWsUrl,
    ],
  );

  return {
    avatarRef,
    sessionState,
    sessionCloseReason,
    stream,
    initAvatar: init,
    startAvatar: start,
    stopAvatar: stop,
  };
};
